import * as XLSX from 'xlsx';
import { Position } from '@shared/schema';

export interface ParsedData {
  positions: Array<{
    id: number;
    originalId: string;
    gewerk: string;
    name: string;
    beschreibung: string;
    einheit: string;
    preis: number;
    umsatzsteuer: number;
    menge: number;
  }>;
  totalItems: number;
  errors: string[];
}

export async function parseExcelFile(file: File): Promise<ParsedData> {
  console.log("Parsing file:", file.name, file.type, file.size);
  
  try {
    // Validate file before processing
    if (!file) {
      throw new Error('Keine Datei ausgewählt');
    }
    
    if (file.size === 0) {
      throw new Error('Die Datei ist leer');
    }
    
    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      throw new Error('Datei ist zu groß (Maximum: 50MB)');
    }

    // Read the file
    const buffer = await file.arrayBuffer();
    
    if (!buffer || buffer.byteLength === 0) {
      throw new Error('Datei konnte nicht gelesen werden');
    }
    
    // Parse with SheetJS
    const workbook = XLSX.read(buffer, { 
      type: 'array',
      cellDates: true,
      dateNF: 'yyyy-mm-dd',
    });
    
    if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
      throw new Error('Keine gültigen Arbeitsblätter in der Datei gefunden');
    }
    
    console.log("Sheets:", workbook.SheetNames);
    
    // Get first sheet
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    
    if (!firstSheet) {
      throw new Error('Arbeitsblatt konnte nicht gelesen werden');
    }
    
    // Convert to JSON with header:1 to get array of arrays
    const data = XLSX.utils.sheet_to_json<any[]>(firstSheet, { 
      header: 1, 
      defval: '',
      blankrows: false 
    });
    
    if (!data || data.length === 0) {
      throw new Error('Keine Daten im Arbeitsblatt gefunden');
    }
    
    console.log("Data rows:", data.length);
    console.log("Sample:", data.slice(0, 3));
    
    // Find headers - look for columns that might be descriptions, units, prices
    const headerRow = findHeaderRow(data);
    console.log("Header row:", headerRow);
    
    if (headerRow === -1) {
      throw new Error('Keine gültigen Spaltenüberschriften gefunden');
    }
    
    // Extract column indexes based on header name
    const columns = extractColumnIndexes(data[headerRow]);
    console.log("Columns mapping:", columns);
    
    // Validate that we have at least a description column
    if (columns.beschreibung === -1) {
      throw new Error('Keine Beschreibungsspalte gefunden');
    }
    
    // Process data rows into positions - limit to 300 rows for performance on mobile
    const limit = 300;
    const positions = processRows(data, headerRow + 1, columns, limit);
    
    if (positions.length === 0) {
      throw new Error('Keine gültigen Positionen in der Datei gefunden');
    }
    
    console.log("Processed positions:", positions.length);
    console.log("First 3 positions:", positions.slice(0, 3));
    
    // Count total available rows for information
    const totalRows = data.length - (headerRow + 1);
    const displayLimit = Math.min(limit, totalRows);
    
    return {
      positions,
      totalItems: positions.length,
      errors: totalRows > displayLimit ? 
        [`Zur besseren Performance werden nur die ersten ${displayLimit} von ${totalRows} Positionen angezeigt`] : []
    };
  } catch (error) {
    console.error("Error parsing Excel file:", error);
    
    // Provide more specific error messages
    let errorMessage = 'Unbekannter Fehler beim Verarbeiten der Datei';
    
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    // Add context for common issues
    if (errorMessage.includes('Unsupported file type')) {
      errorMessage = 'Dateityp wird nicht unterstützt. Bitte verwenden Sie Excel-Dateien (.xlsx, .xls) oder CSV.';
    } else if (errorMessage.includes('Cannot read property')) {
      errorMessage = 'Datei ist beschädigt oder hat ein unerwartetes Format.';
    } else if (errorMessage.includes('out of memory') || errorMessage.includes('Maximum call stack')) {
      errorMessage = 'Datei ist zu groß oder komplex für die mobile Verarbeitung.';
    }
    
    return {
      positions: [],
      totalItems: 0,
      errors: [errorMessage]
    };
  }
}

// Find the row that likely contains headers
function findHeaderRow(data: any[][]): number {
  // Look for rows that contain typical header names
  const headerKeywords = ['beschreibung', 'position', 'preis', 'einheit', 'menge'];
  
  console.log("=== HEADER DETECTION ===");
  for (let i = 0; i < Math.min(10, data.length); i++) {
    const row = data[i];
    if (!row || !row.length) continue;
    
    const rowText = row.join(' ').toLowerCase();
    console.log(`Row ${i}: "${rowText}"`);
    
    if (headerKeywords.some(keyword => rowText.includes(keyword))) {
      console.log(`✓ Found header at row ${i}`);
      return i;
    }
  }
  
  // Look for rows with multiple non-empty cells (likely headers)
  for (let i = 0; i < Math.min(5, data.length); i++) {
    const row = data[i];
    if (!row) continue;
    
    const nonEmptyCells = row.filter(cell => cell && String(cell).trim() !== '').length;
    if (nonEmptyCells >= 3) {
      console.log(`✓ Found potential header at row ${i} with ${nonEmptyCells} columns`);
      return i;
    }
  }
  
  // Return -1 if no valid header found
  console.log("⚠️ No valid header found");
  return -1;
}

// Extract column indexes for each field we care about
function extractColumnIndexes(headerRow: any[]): Record<string, number> {
  const columns: Record<string, number> = {
    id: -1,
    gewerk: -1,
    name: -1,
    beschreibung: -1,
    einheit: -1,
    preis: -1,
    umsatzsteuer: -1,
    menge: -1
  };
  
  // This will help us detect the exact "Preis/Einheit" column
  let preisEinheitIndex = -1;
  
  console.log("Analyzing header row:", headerRow);
  
  headerRow.forEach((header, index) => {
    if (!header) return;
    
    const headerText = String(header).toLowerCase().trim();
    console.log(`Column ${index}:`, headerText);
    
    // Exact match for the specified header format
    if (headerText === 'artikel-id') {
      columns.id = index;
    }
    else if (headerText === 'gewerk') {
      columns.gewerk = index;
    }
    else if (headerText === 'name') {
      columns.name = index;
    }
    else if (headerText === 'beschreibung') {
      columns.beschreibung = index;
    }
    else if (headerText === 'einheit') {
      columns.einheit = index;
    }
    else if (headerText === 'preis/einheit') {
      columns.preis = index;
      preisEinheitIndex = index;
    }
    else if (headerText === 'mwst') {
      columns.umsatzsteuer = index;
    }
    else if (headerText === 'menge') {
      columns.menge = index;
    }
    // Fallback using contains check if exact match fails
    else if ((headerText.includes('id') || headerText.includes('artikel') || 
             headerText.includes('pos') || headerText.includes('nummer')) && columns.id === -1) {
      columns.id = index;
    }
    else if ((headerText.includes('name') || headerText.includes('titel')) && columns.name === -1) {
      columns.name = index;
    }
    else if ((headerText.includes('beschreibung') || headerText.includes('text') || 
             headerText.includes('leistung')) && columns.beschreibung === -1) {
      columns.beschreibung = index;
    }
    else if ((headerText.includes('einheit') || headerText.includes('unit') || 
             headerText.includes('me')) && columns.einheit === -1 && preisEinheitIndex !== index) {
      columns.einheit = index;
    }
    else if ((headerText.includes('preis') || headerText.includes('price') || 
             headerText.includes('kosten')) && columns.preis === -1) {
      columns.preis = index;
    }
    else if ((headerText.includes('gewerk') || headerText.includes('gruppe') || 
             headerText.includes('kategorie')) && columns.gewerk === -1) {
      columns.gewerk = index;
    }
    else if ((headerText.includes('mwst') || headerText.includes('ust') || 
             headerText.includes('steuer')) && columns.umsatzsteuer === -1) {
      columns.umsatzsteuer = index;
    }
    else if ((headerText.includes('menge') || headerText.includes('anzahl') || 
             headerText.includes('quantity')) && columns.menge === -1) {
      columns.menge = index;
    }
  });
  
  // If we couldn't find a description column, use the second column as fallback
  if (columns.beschreibung === -1 && headerRow.length > 1) {
    columns.beschreibung = 1;
  }
  
  // If still no description column found, try to find the longest text column
  if (columns.beschreibung === -1) {
    for (let i = 0; i < headerRow.length; i++) {
      const header = headerRow[i];
      if (header && typeof header === 'string' && header.length > 5) {
        columns.beschreibung = i;
        break;
      }
    }
  }
  
  // If we couldn't find a price column, look for numeric columns
  if (columns.preis === -1) {
    for (let i = 0; i < headerRow.length; i++) {
      const header = headerRow[i];
      if (typeof header === 'number' || 
          (header && !isNaN(Number(String(header).replace(',', '.'))))) {
        columns.preis = i;
        break;
      }
    }
  }
  
  return columns;
}

// Process data rows into positions
function processRows(
  data: any[][], 
  startRow: number, 
  columns: Record<string, number>,
  limit: number = 1000 // Default limit of 1000 rows for performance
): Array<{
  id: number;
  originalId: string;
  gewerk: string;
  name: string;
  beschreibung: string;
  einheit: string;
  preis: number;
  umsatzsteuer: number;
  menge: number;
}> {
  const positions: Array<{
    id: number;
    originalId: string;
    gewerk: string;
    name: string;
    beschreibung: string;
    einheit: string;
    preis: number;
    umsatzsteuer: number;
    menge: number;
  }> = [];
  
  // Process up to limit rows for performance
  const maxRows = Math.min(data.length, startRow + limit);
  console.log(`Processing rows from ${startRow} to ${maxRows - 1}, total data rows: ${data.length}`);
  
  for (let i = startRow; i < maxRows; i++) {
    const row = data[i];
    console.log(`Row ${i}:`, row);
    if (!row || !row.length) {
      console.log(`Skipping empty row ${i}`);
      continue;
    }
    
    // Skip rows that appear to be headers or totals
    const rowText = row.join(' ').toLowerCase();
    if (rowText.includes('summe') || rowText.includes('gesamt') || 
        rowText.includes('total') || rowText.includes('netto')) {
      console.log(`Skipping summary row ${i}: ${rowText}`);
      continue;
    }
    
    // Skip empty rows (where description is empty)
    const description = columns.beschreibung >= 0 ? row[columns.beschreibung] : '';
    
    // For the first few data rows, be more lenient - check if ANY column has meaningful data
    const isEarlyRow = i < startRow + 5;
    const hasAnyData = isEarlyRow && row.some(cell => cell && String(cell).trim() !== '');
    
    if ((!description || String(description).trim() === '') && !hasAnyData) {
      console.log(`Skipping row ${i} - empty description. Description column: ${columns.beschreibung}, value:`, description);
      continue;
    }
    
    // If we have some data but no description, try to use another column as description
    let finalDescription = description;
    if ((!description || String(description).trim() === '') && hasAnyData) {
      // Try to find a non-empty text column to use as description
      for (let col = 0; col < row.length; col++) {
        const cellValue = row[col];
        if (cellValue && String(cellValue).trim() !== '' && typeof cellValue === 'string' && cellValue.length > 2) {
          finalDescription = cellValue;
          console.log(`Using column ${col} as description for row ${i}: "${finalDescription}"`);
          break;
        }
      }
    }
    
    // Debug the current row data
    console.log("Processing row data:", {
      id: columns.id >= 0 ? row[columns.id] : null,
      gewerk: columns.gewerk >= 0 ? row[columns.gewerk] : null,
      name: columns.name >= 0 ? row[columns.name] : null,
      beschreibung: finalDescription,
      einheit: columns.einheit >= 0 ? row[columns.einheit] : null,
      preis: columns.preis >= 0 ? row[columns.preis] : null,
      umsatzsteuer: columns.umsatzsteuer >= 0 ? row[columns.umsatzsteuer] : null,
      menge: columns.menge >= 0 ? row[columns.menge] : null
    });
    
    // Handle Name as a string
    let name = '';
    if (columns.name >= 0) {
      const rawName = row[columns.name];
      name = rawName ? String(rawName).trim() : '';
    }
    
    // Handle Einheit (unit of measurement) as a string
    let einheit = 'Stk'; // Default
    if (columns.einheit >= 0) {
      const rawEinheit = row[columns.einheit];
      einheit = rawEinheit ? String(rawEinheit).trim() : 'Stk';
    }
    
    // Handle Preis/Einheit as a numeric value
    let preis = 0;
    if (columns.preis >= 0) {
      const rawPreis = row[columns.preis];
      preis = parsePrice(rawPreis);
    }
    
    // Handle Menge as a numeric value (preserve original values, default to 0)
    let menge: number = 0; // Default to 0 for no quantity
    if (columns.menge >= 0) {
      const rawMenge = row[columns.menge];
      menge = parseMenge(rawMenge);
    }
    
    // Create position with extracted data
    const position = {
      id: positions.length + 1,
      originalId: columns.id >= 0 ? String(row[columns.id] || '') : `Pos-${positions.length + 1}`,
      gewerk: columns.gewerk >= 0 ? String(row[columns.gewerk] || '') : '',
      name: name,
      beschreibung: String(finalDescription || 'Unbekannte Position'),
      einheit: einheit, // Unit of measurement (m, Stück, etc.)
      preis: preis, // Price per unit (numeric value)
      umsatzsteuer: parseTax(columns.umsatzsteuer >= 0 ? row[columns.umsatzsteuer] : 19),
      menge: menge // Quantity (numeric value)
    };
    
    console.log(`Successfully created position ${positions.length + 1} from row ${i}:`, position);
    positions.push(position);
  }
  
  console.log(`Final positions count: ${positions.length}`);
  return positions;
}

// Parse price values from various formats
function parsePrice(value: any): number {
  // Handle null, undefined, empty strings
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  // If already a number, just return it
  if (typeof value === 'number') {
    return value;
  }
  
  try {
    // Convert to string and clean it
    const valueStr = String(value);
    console.log("Parsing price from:", valueStr);
    
    // Check if it's already a valid number
    const simpleNum = Number(valueStr);
    if (!isNaN(simpleNum)) {
      return simpleNum;
    }
    
    // Handle German number format (comma as decimal separator)
    // Also handle currency symbols and other non-numeric characters
    let priceStr = valueStr
      .replace(/[^\d,.-]/g, '')  // Remove all non-numeric chars except decimal & thousand separators
      .trim();
    
    // Handle different number formats (1.000,00 or 1,000.00)
    if (priceStr.includes(',') && priceStr.includes('.')) {
      // Format like 1.000,00 (German)
      if (priceStr.lastIndexOf(',') > priceStr.lastIndexOf('.')) {
        priceStr = priceStr.replace(/\./g, '').replace(',', '.');
      } 
      // Format like 1,000.00 (English)
      else {
        priceStr = priceStr.replace(/,/g, '');
      }
    } else if (priceStr.includes(',')) {
      // Just comma, treat as decimal separator
      priceStr = priceStr.replace(',', '.');
    }
    
    const price = parseFloat(priceStr);
    console.log(`Converted '${valueStr}' to ${price}`);
    return isNaN(price) ? 0 : price;
  } catch (e) {
    console.error("Error parsing price:", e);
    return 0;
  }
}

// Parse tax percentage values to standardized German VAT rates (0%, 7%, 19%)
function parseTax(value: any): number {
  // Default to 19% if empty, null or undefined
  if (value === null || value === undefined || value === '') {
    return 19;
  }
  
  try {
    // Convert to string and clean it for consistent processing
    const valueStr = String(value).trim();
    console.log("Parsing tax from:", valueStr);
    
    // Handle percentage symbols and convert commas to dots
    const cleanedStr = valueStr.replace(/[^\d,.-]/g, '').replace(',', '.');
    
    // Get numeric value
    let taxValue: number;
    
    // If it's already a number
    if (typeof value === 'number') {
      taxValue = value;
    } else {
      taxValue = parseFloat(cleanedStr);
    }
    
    // If parsing failed, default to 19%
    if (isNaN(taxValue)) {
      console.log(`Tax value "${valueStr}" could not be parsed, defaulting to 19%`);
      return 19;
    }
    
    // If it's a decimal (0.19, 0.07, etc.), convert to percentage
    if (taxValue > 0 && taxValue <= 1) {
      taxValue = Math.round(taxValue * 100);
      console.log(`Converted decimal tax ${valueStr} to ${taxValue}%`);
    }
    
    // Standardize to German VAT rates (0%, 7%, 19%)
    console.log(`Standardizing tax value ${taxValue} to German VAT rate`);
    
    // For better stability, check for approximate values
    if (Math.abs(taxValue) < 1) {
      // Zero or very small values treated as 0%
      return 0;
    } else if (Math.abs(taxValue - 7) <= 1) {
      // Values close to 7 treated as 7%
      return 7;
    } else if (Math.abs(taxValue - 19) <= 2) {
      // Values close to 19 treated as 19%
      return 19;
    } else if (taxValue <= 0) {
      return 0;
    } else if (taxValue < 10) {
      // Values between 0 and 10 treated as 7%
      return 7;
    } else {
      // All other positive values treated as 19%
      return 19;
    }
  } catch (e) {
    console.error("Error parsing tax rate:", e);
    return 19; // Default to 19% on error
  }
}

// Parse quantity (menge) values from various formats
function parseMenge(value: any): number {
  // Handle null, undefined, empty strings - return 0 (no quantity)
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  // If already a number, just return it as-is
  if (typeof value === 'number') {
    return value;
  }
  
  try {
    // Convert to string and clean it
    const valueStr = String(value);
    console.log("Parsing menge from:", valueStr);
    
    // Check if it's already a valid number
    const simpleNum = Number(valueStr);
    if (!isNaN(simpleNum)) {
      return simpleNum;
    }
    
    // Handle German number format (comma as decimal separator)
    let mengeStr = valueStr
      .replace(/[^\d,.-]/g, '')  // Remove all non-numeric chars except decimal & thousand separators
      .trim();
    
    // Handle different number formats (1.000,00 or 1,000.00)
    if (mengeStr.includes(',') && mengeStr.includes('.')) {
      // Format like 1.000,00 (German)
      if (mengeStr.lastIndexOf(',') > mengeStr.lastIndexOf('.')) {
        mengeStr = mengeStr.replace(/\./g, '').replace(',', '.');
      } 
      // Format like 1,000.00 (English)
      else {
        mengeStr = mengeStr.replace(/,/g, '');
      }
    } else if (mengeStr.includes(',')) {
      // Just comma, treat as decimal separator
      mengeStr = mengeStr.replace(',', '.');
    }
    
    const menge = parseFloat(mengeStr);
    console.log(`Converted '${valueStr}' to ${menge}`);
    return isNaN(menge) ? 0 : menge;
  } catch (e) {
    console.error("Error parsing menge:", e);
    return 0;
  }
}